import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import UndoIcon from "@mui/icons-material/Undo";
import { toast } from "react-toastify";

import Header from "../../../components/Header";
import { formatLkr } from "../hpcMembershipPricing";
import {
  durationLabel,
  errorMessage,
  getPromotion,
  reconcileRedemption,
  reverseRedemption,
  setPromotionEnabled,
  typeLabel,
  valueLabel,
} from "./promotionApi";
import { InfoField, Section, StatTile, StatusChip, usePromotionStyles } from "./promotionStyles";

const eligibilityLabel = (row) => {
  const pkg = row.package_category === "family"
    ? "Family"
    : `${row.package_category === "couple" ? "Couple" : "Individual"} ${row.member_type || ""}`.trim();
  return `${pkg} · ${durationLabel(row.duration_code)}`;
};

const DIALOG_TEXT = {
  reverse: {
    title: "Reverse redemption",
    body: "The redemption is marked as reversed. Payments and fees are not changed, and the member cannot use this code again.",
    label: "Reason (required)",
    confirm: "Reverse redemption",
  },
  accept: {
    title: "Accept flagged redemption",
    body: "The discount stays and the redemption counts as redeemed.",
    label: "Note (optional)",
    confirm: "Accept",
  },
  cancel: {
    title: "Cancel flagged redemption",
    body: "The redemption is cancelled. The payment already recorded is not changed.",
    label: "Note (optional)",
    confirm: "Cancel redemption",
  },
};

const HpcPromotionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const styles = usePromotionStyles();
  const { colors } = styles;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getPromotion(id));
    } catch (err) {
      setError(errorMessage(err, "Could not load the promotion"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const closeDialog = () => {
    setDialog(null);
    setReason("");
  };

  const confirmDialog = async () => {
    setBusy(true);
    try {
      if (dialog.kind === "reverse") {
        await reverseRedemption(dialog.redemption.id, reason);
        toast.success("Redemption reversed");
      } else {
        await reconcileRedemption(dialog.redemption.id, dialog.kind, reason);
        toast.success(dialog.kind === "accept" ? "Redemption accepted" : "Redemption cancelled");
      }
      closeDialog();
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not update the redemption"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    const enable = data.promotion.status !== "ACTIVE";
    try {
      await setPromotionEnabled(id, enable);
      toast.success(enable ? "Promotion enabled" : "Promotion disabled");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not change the promotion status"));
    }
  };

  if (error) {
    return <Box m="20px"><Alert severity="error">{error}</Alert></Box>;
  }
  if (!data) {
    return <Box m="20px"><Typography color={colors.grey[100]}>Loading promotion…</Typography></Box>;
  }

  const { promotion, summary, redemptions, audit } = data;
  const flagged = redemptions.filter((r) => r.status === "PENDING" && r.needs_reconciliation);
  const validity = !promotion.valid_from && !promotion.valid_until
    ? "No date limit"
    : `${promotion.valid_from || "Any time"} → ${promotion.valid_until || "No end"}`;
  const text = dialog ? DIALOG_TEXT[dialog.kind] : null;
  const smallSx = { minWidth: 0, px: 1, py: 0.25, fontSize: "0.78rem" };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={styles.pageSx}>
      <Header title={promotion.name} subtitle={`Promotion code ${promotion.code}`} />

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} sx={styles.neutralButtonSx} onClick={() => navigate("/hpc/promotions")}>
          Back to promotions
        </Button>
        <Button variant="contained" startIcon={<EditOutlinedIcon />} sx={styles.primaryButtonSx} onClick={() => navigate(`/hpc/promotions/${id}/edit`)}>
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={promotion.status === "ACTIVE" ? <BlockIcon /> : <CheckCircleOutlineIcon />}
          sx={promotion.status === "ACTIVE" ? styles.dangerButtonSx : styles.successButtonSx}
          onClick={toggle}
        >
          {promotion.status === "ACTIVE" ? "Disable" : "Enable"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AssessmentOutlinedIcon />}
          sx={styles.secondaryButtonSx}
          onClick={() => navigate(`/hpc/promotions/report?promotion_id=${id}`)}
        >
          Report
        </Button>
      </Box>

      {flagged.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {flagged.length} payment{flagged.length > 1 ? "s were" : " was"} recorded after the reservation expired or the
          campaign limit was reached. Accept or cancel each one in the redemption history.
        </Alert>
      )}

      <Section
        title="Summary"
        colors={colors}
        actions={(
          <>
            <StatusChip status={promotion.effective_status} size="medium" />
            {data.locked && (
              <Chip label="Used: terms locked" variant="outlined" sx={{ color: colors.grey[100], borderColor: colors.grey[400] }} />
            )}
          </>
        )}
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <InfoField colors={colors} label="Code" value={<Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>{promotion.code}</Box>} />
          <InfoField colors={colors} label="Type" value={typeLabel(promotion.promotion_type)} />
          <InfoField colors={colors} label="Value" value={valueLabel(promotion)} />
          <InfoField colors={colors} label="Validity" value={validity} />
          <InfoField colors={colors} label="Campaign limit" value={promotion.max_redemptions || "No limit"} />
          <InfoField colors={colors} label="Uses per member" value={promotion.max_per_member} />
          <InfoField colors={colors} label="New members only" value={promotion.new_members_only ? "Yes" : "No"} />
          <InfoField colors={colors} label="Created by" value={promotion.created_by} />
        </Grid>
        <Grid container spacing={1.5}>
          {[
            ["Validation attempts", summary.validation_attempts, colors.blueAccent[400]],
            ["Registrations with code", summary.registrations_with_code, colors.blueAccent[400]],
            ["Redeemed", summary.redeemed, colors.greenAccent[500]],
            ["Pending", summary.pending, "#f5a623"],
            ["Cancelled", summary.cancelled, colors.grey[500]],
            ["Reversed", summary.reversed, colors.redAccent[500]],
            ["Total discount", formatLkr(summary.total_discount), colors.greenAccent[500]],
            ["Total paid", formatLkr(summary.total_paid), colors.greenAccent[500]],
          ].map(([label, value, color]) => (
            <Grid item xs={6} sm={4} md={3} key={label}>
              <StatTile label={label} value={value} color={color} colors={colors} />
            </Grid>
          ))}
        </Grid>
        {promotion.description && (
          <Typography variant="body2" color={colors.grey[200]} sx={{ mt: 2 }}>{promotion.description}</Typography>
        )}
        <Typography variant="body2" fontWeight={700} color={colors.grey[100]} sx={{ mt: 2 }}>Eligible packages</Typography>
        <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.75}>
          {(promotion.eligibility || []).map((row) => (
            <Chip
              key={eligibilityLabel(row)}
              size="small"
              label={eligibilityLabel(row)}
              variant="outlined"
              sx={{ color: colors.grey[100], borderColor: colors.blueAccent[400] }}
            />
          ))}
        </Box>
        {promotion.terms && (
          <Typography variant="body2" color={colors.grey[300]} sx={{ mt: 2 }}>Terms: {promotion.terms}</Typography>
        )}
        {promotion.admin_notes && (
          <Typography variant="body2" color={colors.grey[300]} sx={{ mt: 1 }}>Staff notes: {promotion.admin_notes}</Typography>
        )}
      </Section>

      <Section title="Redemption history" subtitle="Each registration that used this code" colors={colors}>
        {redemptions.length === 0 ? (
          <Alert severity="info">No registrations have used this code yet.</Alert>
        ) : (
          <TableContainer sx={{ overflowX: "auto", border: `1px solid ${colors.grey[700]}`, borderRadius: 1 }}>
            <Table size="small" sx={{ minWidth: 980, ...styles.tableSx }}>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell align="right">Original value</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Final</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied (UTC)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/hpc/members/${r.membership_id}`}
                        sx={{ color: colors.greenAccent[300], fontWeight: 700 }}
                      >
                        {r.full_name || r.membership_id}
                      </Link>
                      <Typography variant="caption" display="block" color={colors.grey[300]}>
                        {r.membership_id}{r.group_id ? ` · ${r.group_id}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{(r.membership_type || "").replace(/_/g, " ")}</TableCell>
                    <TableCell align="right">{formatLkr(r.original_value ?? r.base_membership_fee + r.base_admission_fee)}</TableCell>
                    <TableCell align="right">{formatLkr(r.discount_amount)}</TableCell>
                    <TableCell align="right">{formatLkr(r.final_total)}</TableCell>
                    <TableCell align="right">{r.payment_amount != null ? formatLkr(r.payment_amount) : "—"}</TableCell>
                    <TableCell>
                      <StatusChip status={r.status} />
                      {r.needs_reconciliation && r.status === "PENDING" && (
                        <Typography variant="caption" display="block" sx={{ color: "#f5a623", fontWeight: 600, mt: 0.5 }}>
                          {r.reconciliation_note || "Needs reconciliation"}
                        </Typography>
                      )}
                      {r.status === "REVERSED" && (
                        <Typography variant="caption" display="block" color={colors.grey[300]} mt={0.5}>
                          {r.reversal_reason} ({r.reversed_by})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{(r.applied_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.75} justifyContent="flex-end" flexWrap="wrap">
                        {r.status === "REDEEMED" && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<UndoIcon />}
                            sx={{ ...styles.dangerButtonSx, ...smallSx }}
                            onClick={() => setDialog({ kind: "reverse", redemption: r })}
                          >
                            Reverse
                          </Button>
                        )}
                        {r.status === "PENDING" && r.needs_reconciliation && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CheckCircleOutlineIcon />}
                              sx={{ ...styles.successButtonSx, ...smallSx }}
                              onClick={() => setDialog({ kind: "accept", redemption: r })}
                            >
                              Accept
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<HighlightOffIcon />}
                              sx={{ ...styles.dangerButtonSx, ...smallSx }}
                              onClick={() => setDialog({ kind: "cancel", redemption: r })}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section title="Change history" colors={colors}>
        {(audit || []).length === 0 ? (
          <Typography variant="body2" color={colors.grey[300]}>No changes recorded.</Typography>
        ) : (
          (audit || []).map((entry) => (
            <Typography key={entry.id} variant="body2" color={colors.grey[200]} sx={{ mb: 0.5 }}>
              {(entry.created_at || "").replace("T", " ").slice(0, 16)} · {entry.action} · {entry.actor}
              {entry.reason ? ` · ${entry.reason}` : ""}
            </Typography>
          ))
        )}
      </Section>

      <Dialog open={Boolean(dialog)} onClose={() => !busy && closeDialog()} fullWidth maxWidth="sm">
        <DialogTitle>{text?.title}</DialogTitle>
        <DialogContent dividers sx={styles.pageSx}>
          {dialog && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {dialog.redemption.full_name || dialog.redemption.membership_id} · {dialog.redemption.membership_id} ·{" "}
              discount {formatLkr(dialog.redemption.discount_amount)}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>{text?.body}</Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={text?.label}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button variant="outlined" sx={styles.neutralButtonSx} onClick={closeDialog} disabled={busy}>
            Close
          </Button>
          <Button
            variant="contained"
            sx={dialog?.kind === "accept" ? styles.successContainedSx : styles.dangerContainedSx}
            onClick={confirmDialog}
            disabled={busy || (dialog?.kind === "reverse" && !reason.trim())}
          >
            {text?.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HpcPromotionDetails;
